import { useState } from 'react';
import { useTheme } from '@mui/material/styles';
import {
	Box,
	Typography,
	Paper,
	Tabs,
	Tab,
	Snackbar,
	Alert,
} from '@mui/material';
import DealsSettings from '../components/Settings/DealsSettings/DealsSettings';
import PipelineSettings from '../components/Settings/PipelineSettings';
import PromptWorkflows from '../components/Settings/PromptWorkflows';
import MonitorAlerts from '../components/Settings/MonitorAlerts/MonitorAlerts';

/**
 * Settings page for the Investing app
 * Allows configuring app-specific settings and preferences
 */
const SettingsPage = () => {
	const theme = useTheme();
	const [value, setValue] = useState(0);

	const handleChange = (event, newValue) => {
		setValue(newValue);
	};

	const [notification, setNotification] = useState({
		open: false,
		message: '',
		severity: 'success',
		autoHideDuration: 3000,
	});

	const showNotification = (message, severity = 'success') => {
		setNotification({ open: true, message, severity });
	};

	const handleCloseNotification = () => {
		setNotification({ ...notification, open: false });
	};

	return (
		<>
			<Box sx={{ p: 2 }}>
				<Paper
					sx={{
						p: 2,
						backgroundColor: theme.palette.background.paper,
					}}
				>
					<Typography variant='h4' gutterBottom>
						Investing App Configuration
					</Typography>
					<Tabs
						value={value}
						onChange={handleChange}
						variant='scrollable'
						scrollButtons='auto'
						sx={{
							my: 2,
							borderBottom: '1px solid',
							borderColor: 'divider',
						}}
					>
						<Tab label='Pipeline Settings' />
						<Tab label='Deals Configuration' />
						<Tab label='Monitor Alerts' />
						<Tab label='Prompt Workflows' />
						<Tab label='Access Control' />
					</Tabs>

					<Box sx={{ mt: 2 }}>
						{value === 0 && (
							<Box>
								<Typography
									variant='body2'
									color='text.secondary'
									sx={{ mb: 2 }}
								>
									Configure pipeline stages, fields, and visualization options
								</Typography>
								<PipelineSettings showNotification={showNotification} />
							</Box>
						)}
						{value === 1 && (
							<Box>
								<Typography
									variant='body2'
									color='text.secondary'
									sx={{ mb: 2 }}
								>
									Configure deal metadata, templates, and workflow options.
								</Typography>
								<DealsSettings showNotification={showNotification} />
							</Box>
						)}
						{value === 2 && (
							<Box>
								<Typography
									variant='body2'
									color='text.secondary'
									sx={{ mb: 2 }}
								>
									Configure monitor source types and refresh cadences for
									alerts.
								</Typography>
								<MonitorAlerts showNotification={showNotification} />
							</Box>
						)}
						{value === 3 && (
							<Box>
								<Typography
									variant='body2'
									color='text.secondary'
									sx={{ mb: 2 }}
								>
									Manage your organization&apos;s prompt templates and defaults.
								</Typography>
								<PromptWorkflows showNotification={showNotification} />
							</Box>
						)}
						{value === 4 && (
							<Box>
								<Typography
									variant='body2'
									color='text.secondary'
									sx={{ mb: 2 }}
								>
									Manage user permissions for the Investing app features.
								</Typography>
							</Box>
						)}
					</Box>
				</Paper>
			</Box>

			{/* Notifications for settings */}
			<Snackbar
				open={notification.open}
				onClose={handleCloseNotification}
				anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
				autoHideDuration={notification.autoHideDuration || 3000}
			>
				<Alert
					onClose={handleCloseNotification}
					severity={notification.severity}
					variant='filled'
					sx={{ width: '100%' }}
				>
					{notification.message}
				</Alert>
			</Snackbar>
		</>
	);
};

export default SettingsPage;
